import isEqual from 'lodash/isEqual';
import { ChevronDownIcon, PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { ComponentPropsWithoutRef, forwardRef, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { Dialog } from '@/components/core/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { Separator } from '@/components/core/separator';
import { Skeleton } from '@/components/core/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { ConfirmDeleteViewDialogContent } from '@/components/data-table/views/confirm-delete-view-dialog-content';
import { ViewNameDialogContent } from '@/components/data-table/views/view-name-dialog-content';
import { FormDialog } from '@/components/forms/form-dialog';
import { cn } from '@/shared/helpers';
import { Result } from '@/shared/helpers/Result';
import { useUpdateEffect } from '@/shared/hooks/useUpdateEffect';

import { FilterDefs, FilterState } from '../filters/types';
import { AddViewInput, UpdateViewInput, View } from './types';

type Props = {
  /** Available views to display as selectable view options. */
  views: View[];

  /**
   * Any of:
   * - the view selected by the user
   * - a default view (if no ID was specified)
   * - `undefined` if saved views are still loading and a view has not yet been matched by ID
   * - a fallback view if no views could be matched
   */
  currentView?: View;

  /** The current filter state for the table. This will be used when creating new views. */
  filterState?: FilterState<FilterDefs>;

  /** The total row count for the current view. If provided, it will be displayed in a badge next to the view name. */
  rowCount?: number;

  /** Whether the current view's row count is loading and should reserve badge space. */
  isLoadingRowCount?: boolean;

  /**
   * Called when a different view is selected. Should be used to update `currentView`.
   * This component assumes that calling this again with the current view's ID will still result in the filter state
   * being reset to that view's filters.
   */
  onChangeViewId: (viewId: string) => void;

  /** Called when a new view is created. */
  onAddView: (input: AddViewInput) => Promise<Result<Pick<View, 'id'>>>;

  /** Called when an existing view is renamed or its filters are updated. */
  onUpdateView: (input: UpdateViewInput) => Promise<Result>;

  /** Called when an existing view is deleted. */
  onDeleteView: (id: string) => Promise<Result>;
};

const formatByFactor = (x: number, factor: number) => {
  const roundedNum = Math.round((x / factor) * 10) / 10;
  if (roundedNum >= 10) return Math.round(roundedNum).toString();
  return roundedNum.toFixed(1).replace(/\.0$/, '');
};

function formatCount(count: number) {
  if (count < 10000) return Math.round(count).toString();
  if (count < 999_500) return `${formatByFactor(count, 1000)}K`;
  return `${formatByFactor(count, 1_000_000)}M`;
}

type ViewMenuItemProps = Omit<
  ComponentPropsWithoutRef<typeof Button>,
  'children' | 'type' | 'variant'
> & {
  view: View;
  isActive: boolean;
  rowCount?: number;
  isLoadingRowCount?: boolean;
  showDropdownIndicator?: boolean;
};

const ViewMenuItem = forwardRef<HTMLButtonElement, ViewMenuItemProps>(
  (
    { view, isActive, rowCount, isLoadingRowCount, showDropdownIndicator, className, ...props },
    ref,
  ) => (
    <Button
      type="button"
      variant={isActive ? 'outline' : 'ghost'}
      className={cn(
        'group h-8 gap-1.5 rounded-lg px-2.5 text-sm',
        !isActive && 'text-muted-foreground hover:text-foreground',
        className,
      )}
      ref={ref}
      aria-label={view.name}
      {...props}
    >
      <span className="min-w-0 truncate group-data-[state=inactive]:max-w-36">{view.name}</span>
      {isActive &&
        (isLoadingRowCount ? (
          <Skeleton aria-label="Loading row count" className="h-4 w-6" />
        ) : (
          rowCount !== undefined && (
            <Badge className="h-4 bg-muted px-1 text-xs font-medium text-muted-foreground">
              {formatCount(rowCount)}
            </Badge>
          )
        ))}
      {showDropdownIndicator && <ChevronDownIcon className="size-3.5" />}
    </Button>
  ),
);
ViewMenuItem.displayName = 'ViewMenuItem';

export function DataTableViewMenu({
  views,
  currentView,
  filterState,
  rowCount,
  isLoadingRowCount,
  onChangeViewId,
  onAddView,
  onDeleteView,
  onUpdateView,
}: Props) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [renamingView, setRenamingView] = useState<View>();
  const [deletingView, setDeletingView] = useState<View>();
  const [isSavingView, setIsSavingView] = useState(false);

  const hasUnsavedChanges = useMemo(
    () => !isEqual(currentView?.state?.filters ?? [], filterState ?? []),
    [currentView, filterState],
  );

  // Ensures focus moves properly when the current view changes to a dropdown trigger.
  const dropdownRef = useRef<HTMLButtonElement | null>(null);
  useUpdateEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.focus();
    }
  }, [currentView]);

  const addView = async (name: string) => {
    const result = await onAddView({ name, state: { filters: filterState ?? [] } });
    if (result.isSuccess && result.value) {
      // switch to the newly created view
      onChangeViewId(result.value.id);
    }
    return result;
  };

  const renameView = async (name: string) => {
    if (!renamingView) return Result.fail('No view to rename');
    const result = await onUpdateView({ id: renamingView.id, name });
    if (result.isSuccess) {
      setRenamingView(undefined);
    }
    return result;
  };

  const saveFilters = async () => {
    if (!currentView) return;
    setIsSavingView(true);
    await onUpdateView({
      id: currentView.id,
      state: { filters: filterState ?? [] },
    });
    setIsSavingView(false);
  };

  const deleteView = async () => {
    if (!deletingView) return;
    const result = await onDeleteView(deletingView.id);
    if (result.isSuccess) {
      // switch to the first view if the current view was deleted
      if (deletingView.id === currentView?.id && views.length > 1) {
        onChangeViewId(views[0]?.id ?? '');
      }
      setDeletingView(undefined);
    }
  };

  const revertFilters = () => {
    if (currentView?.id) {
      onChangeViewId(currentView.id);
    }
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <TabsPrimitive.Root
          value={
            currentView?.id ??
            // currentView is undefined while saved views load; use '' to keep the component controlled.
            ''
          }
          onValueChange={onChangeViewId}
          orientation="horizontal"
        >
          <TabsPrimitive.List
            aria-label="Table views"
            className="flex min-w-0 flex-wrap items-center gap-2"
          >
            {views.map((view) => {
              const hasDropdown = view.id === currentView?.id && view.isSaved && !isAddDialogOpen;
              const isActive = view.id === currentView?.id;

              return hasDropdown ? (
                <DropdownMenu key={view.id}>
                  <TabsPrimitive.Trigger
                    value={view.id}
                    aria-controls={undefined}
                    ref={dropdownRef}
                    asChild
                  >
                    <DropdownMenuTrigger asChild>
                      <ViewMenuItem
                        view={view}
                        isActive={isActive}
                        rowCount={rowCount}
                        isLoadingRowCount={isLoadingRowCount}
                        showDropdownIndicator
                      />
                    </DropdownMenuTrigger>
                  </TabsPrimitive.Trigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onSelect={() => setRenamingView(view)}>
                      <PencilIcon /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem variant="destructive" onSelect={() => setDeletingView(view)}>
                      <TrashIcon /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <TabsPrimitive.Trigger
                  key={view.id}
                  value={view.id}
                  aria-controls={undefined}
                  asChild
                >
                  <ViewMenuItem
                    view={view}
                    isActive={isActive}
                    rowCount={rowCount}
                    isLoadingRowCount={isLoadingRowCount}
                  />
                </TabsPrimitive.Trigger>
              );
            })}
          </TabsPrimitive.List>
        </TabsPrimitive.Root>

        <Separator orientation="vertical" className="data-[orientation=vertical]:h-6" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setIsAddDialogOpen(true)}
              aria-label="Add view"
              className="text-muted-foreground hover:text-foreground"
            >
              <PlusIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Add view</TooltipContent>
        </Tooltip>
      </div>

      <FormDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <ViewNameDialogContent onSubmit={addView} />
      </FormDialog>
      <FormDialog
        open={!!renamingView}
        onOpenChange={(open) => !open && setRenamingView(undefined)}
      >
        <ViewNameDialogContent view={renamingView} onSubmit={renameView} />
      </FormDialog>
      <Dialog open={!!deletingView} onOpenChange={(open) => !open && setDeletingView(undefined)}>
        <ConfirmDeleteViewDialogContent view={deletingView} onDelete={deleteView} />
      </Dialog>

      {hasUnsavedChanges && (
        <div className="hidden items-center gap-2 sm:flex">
          <Button size="sm" variant="ghost" onClick={revertFilters} disabled={isSavingView}>
            Revert
          </Button>
          {currentView?.isSaved ? (
            <Button size="sm" variant="outline" onClick={saveFilters} loading={isSavingView}>
              Save
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setIsAddDialogOpen(true)}>
              Save as
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
